import { Result, URI } from "@adviser/cement";
import {
  NextId,
  MsgBase,
  MsgWithError,
  buildRes,
  ReqSignedUrlParam,
  buildReqSignedUrl,
  GwCtx,
  MsgIsTenantLedger,
  MsgTypesCtx,
  PreSignedMsg,
  MsgWithConn,
  ResSignedUrlSchema,
} from "./msg-types.js";
import { z } from "zod/v4";

export function buildReqGetData(sthis: NextId, sup: ReqSignedUrlParam, ctx: GwCtx): ReqGetData {
  return buildReqSignedUrl<ReqGetData>(sthis, "reqGetData", sup, ctx);
}

export function MsgIsReqGetData(msg: MsgBase): msg is ReqGetData {
  return msg.type === "reqGetData";
}

export function MsgIsResGetData(msg: MsgBase): msg is ResGetData {
  return msg.type === "resGetData" && MsgIsTenantLedger(msg);
}

export interface CalculatePreSignedUrl {
  calculatePreSignedUrl(ctx: MsgTypesCtx, p: PreSignedMsg): Promise<Result<URI>>;
}

export function buildResGetData(
  msgCtx: MsgTypesCtx,
  req: MsgWithConn<ReqGetData>,
  ctx: CalculatePreSignedUrl,
): Promise<MsgWithError<ResGetData>> {
  return buildRes<MsgWithConn<ReqGetData>, ResGetData>(
    { method: "GET", store: req.methodParam.store },
    "resGetData",
    msgCtx,
    req,
    ctx,
  );
}

export function MsgIsReqPutData(msg: MsgBase): msg is ReqPutData {
  return msg.type === "reqPutData";
}

export function buildReqPutData(sthis: NextId, sup: ReqSignedUrlParam, ctx: GwCtx): ReqPutData {
  return buildReqSignedUrl<ReqPutData>(sthis, "reqPutData", sup, ctx);
}

export function MsgIsResPutData(msg: MsgBase): msg is ResPutData {
  return msg.type === "resPutData";
}

export function buildResPutData(
  msgCtx: MsgTypesCtx,
  req: MsgWithConn<ReqPutData>,
  ctx: CalculatePreSignedUrl,
): Promise<MsgWithError<ResPutData>> {
  return buildRes<MsgWithConn<ReqPutData>, ResPutData>(
    { method: "PUT", store: req.methodParam.store },
    "resPutData",
    msgCtx,
    req,
    ctx,
  );
}

export function MsgIsReqDelData(msg: MsgBase): msg is ReqDelData {
  return msg.type === "reqDelData";
}

export function buildReqDelData(sthis: NextId, sup: ReqSignedUrlParam, ctx: GwCtx): ReqDelData {
  return buildReqSignedUrl<ReqDelData>(sthis, "reqDelData", sup, ctx);
}

export function MsgIsResDelData(msg: MsgBase): msg is ResDelData {
  return msg.type === "resDelData";
}

export function buildResDelData(
  msgCtx: MsgTypesCtx,
  req: MsgWithConn<ReqDelData>,
  ctx: CalculatePreSignedUrl,
): Promise<MsgWithError<ResDelData>> {
  return buildRes<MsgWithConn<ReqDelData>, ResDelData>(
    { method: "DELETE", store: req.methodParam.store },
    "resDelData",
    msgCtx,
    req,
    ctx,
  );
}

// ============================================================================
// Zod Schemas for Data Messages
// ============================================================================

// ReqGetData and ResGetData
export const ReqGetDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("reqGetData"),
});

export type ReqGetData = z.infer<typeof ReqGetDataSchema>;

export const ResGetDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("resGetData"),
});

export type ResGetData = z.infer<typeof ResGetDataSchema>;

// ReqPutData and ResPutData
export const ReqPutDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("reqPutData"),
});

export type ReqPutData = z.infer<typeof ReqPutDataSchema>;

export const ResPutDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("resPutData"),
});

export type ResPutData = z.infer<typeof ResPutDataSchema>;

// ReqDelData and ResDelData
export const ReqDelDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("reqDelData"),
});

export type ReqDelData = z.infer<typeof ReqDelDataSchema>;

export const ResDelDataSchema = ResSignedUrlSchema.extend({
  type: z.literal("resDelData"),
});

export type ResDelData = z.infer<typeof ResDelDataSchema>;
